/**
 * One letter's signature through YouSign: where it stands, and sending, cancelling and the signed
 * PDF. The proxy decides everything (proxy-server/lib/referenceLetterSignature.js); this holds what
 * it last said. A state not yet read is `null`, and nothing is offered on it: offering "Send" on a
 * letter that is already out would only earn a refusal.
 *
 *   - A read that started before a send or cancel is dropped when it lands: the slower answer is
 *     the older one, and showing it would bring back a Send button that the proxy would refuse.
 *   - While the state is `sending` or `uncertain`, or filing in Personio is `pending`, it is read
 *     again every `RECHECK_MS`: those settle on the proxy within minutes, and the page offers no
 *     action until they do.
 *   - `onChange` is told after a send, a cancel or a Personio refile, so the leaver list behind the
 *     draft can reload.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { cancelSignature, downloadSignedPdf, fetchSignature, markLetterDone, refileInPersonio, SignatureError, sendForSignature } from '../../services/referenceLetterApi';
import type { DraftRequestBody, SignatureView } from '../../types/referenceLetterDraft';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';

type Busy = 'send' | 'cancel' | 'pdf' | 'personio' | null;
export const RECHECK_MS = 30_000;
const UNSETTLED = new Set(['sending', 'uncertain']);

/** The caller keys the workspace by `recordId`, so another request starts from fresh state. */
export function useLetterSignature(recordId: string, { onChange }: { onChange?: () => void } = {}) {
  const [view, setView] = useState<SignatureView | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by every send and cancel; a read that started under an older value is stale.
  const mutations = useRef(0);
  // Bumped to read the state again (the retry after a failed read, and the re-check).
  const [readCount, setReadCount] = useState(0);

  useEffect(() => {
    let current = true;
    const startedAt = mutations.current;
    fetchSignature(recordId)
      .then((v) => {
        if (!current || startedAt !== mutations.current) return;
        setView(v);
        setError(null);
      })
      .catch((err: Error) => current && startedAt === mutations.current && setError(`The signature status could not be read: ${err.message}`));
    return () => {
      current = false;
    };
  }, [recordId, readCount]);

  const state = view?.state;
  const unsettled = Boolean(state && UNSETTLED.has(state)) || view?.personio === 'pending';
  useEffect(() => {
    if (!unsettled) return undefined;
    const timer = window.setTimeout(() => setReadCount((n) => n + 1), RECHECK_MS);
    return () => window.clearTimeout(timer);
  }, [unsettled, readCount]);

  /** Reads the state again, e.g. after a read that failed. */
  const reload = useCallback(() => setReadCount((n) => n + 1), []);

  /**
   * A view from a send, cancel, refile or refusal: those answers say nothing about a Done mark (only
   * the status read and the mark itself do), so the mark on screen is kept rather than dropped.
   */
  const adopt = useCallback((next: SignatureView) => {
    setView((v) => ({ ...next, done: next.done !== undefined ? next.done : v?.done ?? null }));
  }, []);

  /** A refusal that knows the letter's state shows it: "already out" is news about the letter. */
  const fail = useCallback((err: unknown, fallback: string) => {
    if (err instanceof SignatureError && err.signature) adopt(err.signature);
    setError(err instanceof Error ? err.message : fallback);
  }, [adopt]);

  /** `request` is the one the preview on screen came from, never a newer one still in its debounce. */
  const send = async (request: DraftRequestBody, template: string) => {
    mutations.current += 1;
    setBusy('send');
    setError(null);
    try {
      adopt(await sendForSignature(request));
      void logAnalyticsEvent('reference_letters_sent_for_signature', { template });
    } catch (err) {
      fail(err, 'The letter could not be sent for signature');
    } finally {
      setBusy(null);
      onChange?.();
    }
  };

  /** Withdraws the letter while it is out; the proxy answers with the new state. */
  const cancel = async (template: string) => {
    mutations.current += 1;
    setBusy('cancel');
    setError(null);
    try {
      adopt(await cancelSignature(recordId));
      void logAnalyticsEvent('reference_letters_signature_cancelled', { template });
    } catch (err) {
      fail(err, 'The signature request could not be cancelled');
    } finally {
      setBusy(null);
      onChange?.();
    }
  };

  // A second click before the button re-renders disabled would earn a refusal shown beside the result.
  const refiling = useRef(false);

  /** Files the signed letter in Personio again after a filing that certainly failed. */
  const refile = async (template: string) => {
    if (refiling.current) return;
    refiling.current = true;
    mutations.current += 1;
    setBusy('personio');
    setError(null);
    try {
      const next = await refileInPersonio(recordId);
      adopt(next);
      void logAnalyticsEvent('reference_letters_personio_refiled', { template, outcome: next.personio ?? 'unknown' });
    } catch (err) {
      fail(err, 'The letter could not be filed in Personio');
    } finally {
      refiling.current = false;
      setBusy(null);
      // The leaver list behind the draft carries the Personio badge too.
      onChange?.();
    }
  };

  /**
   * Marks the letter Done by hand or takes the mark back. Throws for the menu to show; the list
   * behind the draft reloads, since it shows the status too.
   */
  const setDone = async (done: boolean) => {
    // A status read already on its way would land after this and put the old mark back.
    mutations.current += 1;
    const mark = await markLetterDone({ recordId }, done);
    void logAnalyticsEvent('reference_letters_marked_done', { surface: 'header', done });
    setView((v) => (v ? { ...v, done: mark } : v));
    onChange?.();
  };

  /** Saves the signed PDF under `fileName`. */
  const downloadPdf = async (fileName: string, template: string) => {
    setBusy('pdf');
    setError(null);
    try {
      const blob = await downloadSignedPdf(recordId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      void logAnalyticsEvent('reference_letters_signed_pdf_downloaded', { template });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The signed letter could not be downloaded');
    } finally {
      setBusy(null);
    }
  };

  return { view, busy, error, send, cancel, downloadPdf, refile, setDone, reload };
}

export type LetterSignature = ReturnType<typeof useLetterSignature>;
